import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { authAPI } from '../utils/api';
import { toast } from 'react-toastify';
import { LocalizedLink } from '../components/LocalizedLink';
import NonIndexableHelmet from '../components/NonIndexableHelmet';
import { getAuthMeta } from '../data/nonIndexableMeta';
import { useLocale } from '../contexts/LocaleContext';

const LoginPage = () => {
  const { t } = useTranslation('auth');
  const { locale } = useLocale();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [formToken, setFormToken] = useState('');

  useEffect(() => {
    authAPI.getFormToken()
      .then((res) => setFormToken(res.data.token))
      .catch((err) => console.error('Failed to fetch form token:', err));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSuccess(false);
    setError('');
    setResendSuccess(false);

    try {
      await authAPI.sendMagicLink(email);
      setSuccess(true);
      toast.success(t('login.toastSent'));
    } catch (err) {
      console.error('Error sending magic link:', err);
      const errorCode = err.response?.data?.code;
      const errorMessage = err.response?.data?.message || 'Error';

      if (errorCode === 'USER_NOT_FOUND') {
        setError('USER_NOT_FOUND');
        toast.error(t('login.toastNoAccount'));
      } else if (errorCode === 'ACCOUNT_NOT_ACTIVATED') {
        setError('ACCOUNT_NOT_ACTIVATED');
        toast.error(t('login.toastNotActivated'));
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page-gradient">
      <NonIndexableHelmet
        title={locale === 'en' ? 'Sign in | Assistance Video' : getAuthMeta('/login').title}
        description={
          locale === 'en'
            ? 'Sign in to your Assistance Video account to create video sessions, manage credits and access your remote support dashboard.'
            : getAuthMeta('/login').description
        }
      />

      <Container>
        <Row className="justify-content-center align-items-center auth-page-shell">
          <Col xs={12} lg={6} xl={5}>
            <Card className="shadow-lg border-0 rounded-lg auth-page-card">
              <Card.Body className="p-4 p-md-5">
                <div className="text-center mb-4">
                  <div style={{ fontSize: '3rem', color: '#0066FF' }}>
                    <i className="bi bi-camera-video-fill" />
                  </div>
                  <h2 className="fw-bold mt-3">Assistance Video</h2>
                  <p className="text-muted">{t('login.subtitle')}</p>
                </div>

                {success ? (
                  <Alert variant="success">
                    <Alert.Heading>
                      <i className="bi bi-check-circle-fill me-2" />
                      {t('login.successTitle')}
                    </Alert.Heading>
                    <p className="mb-0">
                      {t('login.successLead')}
                      <strong>{email}</strong>
                      {t('login.successTrail')}
                    </p>
                    <hr />
                    <p className="mb-0 small">
                      <i className="bi bi-info-circle me-2" />
                      {t('login.valid15')}
                    </p>
                  </Alert>
                ) : (
                  <>
                    <Alert variant="info" className="border-0">
                      <i className="bi bi-magic me-2" />
                      <strong>{t('login.magicTitle')}</strong>
                      <p className="mb-0 mt-2 small">{t('login.magicBody')}</p>
                    </Alert>

                    {error === 'USER_NOT_FOUND' && (
                      <Alert variant="warning" className="border-0">
                        <i className="bi bi-exclamation-triangle me-2" />
                        <strong>{t('login.userNotFoundTitle')}</strong>
                        <p className="mb-2 mt-2 small">{t('login.userNotFoundBody')}</p>
                        <LocalizedLink to="/register/" className="btn btn-warning btn-sm">
                          <i className="bi bi-person-plus me-2" />
                          {t('login.createBtn')}
                        </LocalizedLink>
                      </Alert>
                    )}

                    {error === 'ACCOUNT_NOT_ACTIVATED' && (
                      <Alert variant="warning" className="border-0">
                        <i className="bi bi-envelope-exclamation me-2" />
                        <strong>{t('login.inactiveTitle')}</strong>
                        <p className="mb-2 mt-2 small">{t('login.inactiveBody')}</p>
                        {resendSuccess ? (
                          <Alert variant="success" className="mb-0 mt-2 small">
                            <i className="bi bi-check-circle me-2" />
                            {t('login.resendSuccess')}
                          </Alert>
                        ) : (
                          <Button
                            variant="warning"
                            size="sm"
                            onClick={async () => {
                              setResendLoading(true);
                              setResendSuccess(false);
                              try {
                                if (!formToken) {
                                  toast.error(t('login.resendTokenError'));
                                  return;
                                }
                                await authAPI.resendVerificationEmail(email, formToken);
                                setResendSuccess(true);
                                toast.success(t('login.resendToastOk'));
                              } catch (err) {
                                console.error(err);
                                const errorMsg = err.response?.data?.message || 'Error';
                                toast.error(errorMsg);
                              } finally {
                                setResendLoading(false);
                              }
                            }}
                            disabled={resendLoading}
                          >
                            {resendLoading ? (
                              <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                {t('login.submit')}
                              </>
                            ) : (
                              <>
                                <i className="bi bi-envelope-arrow-up me-2" />
                                {t('login.resendBtn')}
                              </>
                            )}
                          </Button>
                        )}
                      </Alert>
                    )}

                    <Form onSubmit={handleSubmit}>
                      <Form.Group className="mb-4">
                        <Form.Label className="fw-semibold">
                          <i className="bi bi-envelope me-2" />
                          {t('login.emailLabel')}
                        </Form.Label>
                        <Form.Control
                          type="email"
                          placeholder={t('login.emailPh')}
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          required
                          size="lg"
                          disabled={loading}
                        />
                      </Form.Group>

                      <Button
                        variant="primary"
                        type="submit"
                        size="lg"
                        className="w-100 fw-semibold"
                        disabled={loading || !email}
                      >
                        {loading ? (
                          <>
                            <Spinner animation="border" size="sm" className="me-2" />
                            {t('login.sending')}
                          </>
                        ) : (
                          <>
                            <i className="bi bi-send-fill me-2" />
                            {t('login.submit')}
                          </>
                        )}
                      </Button>
                    </Form>

                    <div className="text-center mt-4">
                      <p className="text-muted mb-0">
                        {t('login.firstVisit')}{' '}
                        <LocalizedLink to="/register/" className="text-decoration-none fw-semibold">
                          {t('login.createAccount')}
                        </LocalizedLink>
                      </p>
                    </div>
                  </>
                )}

                <hr className="my-4" />

                <div className="text-center">
                  <LocalizedLink to="/" className="text-decoration-none">
                    <i className="bi bi-arrow-left me-2" />
                    {t('login.home')}
                  </LocalizedLink>
                </div>
              </Card.Body>
            </Card>

            <div className="text-center mt-3 mt-md-4 text-white">
              <p className="small">
                <i className="bi bi-shield-check me-2" />
                {t('login.footerSecure')}
              </p>
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default LoginPage;
